import { format } from 'date-fns';
import { get as _get } from 'lodash';
import { v4 as uuid } from 'uuid';
import { DriverDefaults } from '../Constants/DriverConstants';
import {
  ClaimantContactPayloadModel,
  ClaimGlassOnly,
  CreateDraftClaimModel,
  FnolQuestionTypes,
  LossLocationAddress,
  SubmitQuestionApiModel,
  SubmitQuestionStoreModel,
  ThirdPartyContactPayloadModel,
  VehicleDetails,
} from '../model/create-draft-claim-request';
import { DamageTypes, GenericProductType, OpCo } from '../types/type';
import { checkAndReplaceDummyPhoneNumber } from '../utils/Utilities';

export function buildDraftClaimPayload(
  customerInfo: any,
  fnolInfo: any,
  dateTime: any,
  partnerId: string,
  mobileSourceSystem?: string,
  options?: {
    addReportedBy?: boolean;
    creatingDraft?: boolean;
    addVehicleDetails?: boolean;
    reportedBy?: number;
    policySource?: string;
  }
): CreateDraftClaimModel {
  const createDraftClaimPayload: CreateDraftClaimModel = {
    lobCode:
      _get(fnolInfo, 'riskInfo.generalizedProductType') === GenericProductType.Home
        ? 'HomeownersLine_HOE'
        : 'PersonalAutoLine',
    lossDate: dateTime.lossDate,
    openDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
    lossLocationAddress: buildLossLocationAddress(fnolInfo, dateTime),
    policySource:
      options?.policySource !== undefined ? options?.policySource : _get(fnolInfo, 'riskInfo.policySource', ''),
    policyNumber: _get(fnolInfo, 'riskInfo.policyNumber'),
    reportedOn: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    reportedTime: '',
    sourceSystem: getSourceSystem(partnerId, mobileSourceSystem),
  };

  const primaryNamedInsured = _get(fnolInfo, 'riskInfo.policy.policyRoles', []).find((role: any) => {
    return role.roleType === 'PrimaryNamedInsured';
  });

  const isReporterPNI =
    !!primaryNamedInsured &&
    isSamePerson(
      _get(primaryNamedInsured, 'firstName', ''),
      _get(primaryNamedInsured, 'lastName', ''),
      _get(customerInfo, 'firstName', ''),
      _get(customerInfo, 'lastName', '')
    ) &&
    _get(fnolInfo, 'reporterInfo.relationshipToInsured') === 'Self';

  const isReporterOnPolicy =
    _get(fnolInfo, 'riskInfo.policy.policyRoles', []).some((role: any) =>
      isSamePerson(
        role.firstName,
        role.lastName,
        _get(customerInfo, 'firstName', ''),
        _get(customerInfo, 'lastName', '')
      )
    ) && _get(fnolInfo, 'reporterInfo.relationshipToInsured') === 'Self';

  const isThirdParty = partnerId.toLowerCase().includes('thirdparty');

  const sourceSystemIdentifier = _get(fnolInfo, 'riskInfo.policyNumber', '');

  // add claim contacts and reportedBy
  if (isReporterPNI) {
    createDraftClaimPayload.claimContacts = [
      {
        contact: {
          contactId: 1,
          contactType: 'Person',
          firstName: primaryNamedInsured.firstName,
          sourceSystemIdentifier: sourceSystemIdentifier,
          isThirdParty: 'NO',
          lastName: primaryNamedInsured.lastName,
          newPrimaryPhoneEmail: {
            country: 'United States',
            area: _get(customerInfo, 'phoneNumber', '')
              .replace(/[^0-9]/g, '')
              .slice(0, 3),
            number: _get(customerInfo, 'phoneNumber', '')
              .replace(/[^0-9]/g, '')
              .slice(3, 10),
            type: 'Cell',
            emailAddress: customerInfo.emailAddress,
            emailType: 'Primary',
          },
        },
      },
    ];
    createDraftClaimPayload.reportedBy = {
      contactId: 1,
    };
    createDraftClaimPayload.insuredContactInfo = {
      contactId: 1,
    };
    createDraftClaimPayload.reportedByType = 'Self';
  } else if (partnerId === 'amfamagency') {
    createDraftClaimPayload.claimContacts = [
      {
        contact: {
          contactId: 1,
          contactType: 'Person',
          firstName: customerInfo.firstName,
          isThirdParty: 'NO',
          lastName: customerInfo.lastName,
        },
      },
      {
        contact: {
          contactId: 2,
          contactType: 'Person',
          firstName: primaryNamedInsured.firstName,
          sourceSystemIdentifier: sourceSystemIdentifier,
          isThirdParty: 'NO',
          lastName: primaryNamedInsured.lastName,
          newPrimaryPhoneEmail: {
            country: 'United States',
            area: _get(customerInfo, 'phoneNumber', '')
              .replace(/[^0-9]/g, '')
              .slice(0, 3),
            number: _get(customerInfo, 'phoneNumber', '')
              .replace(/[^0-9]/g, '')
              .slice(3, 10),
            type: 'Cell',
          },
        },
      },
    ];
    createDraftClaimPayload.reportedBy = {
      contactId: 1,
    };
    createDraftClaimPayload.insuredContactInfo = {
      contactId: 2,
    };
    createDraftClaimPayload.reportedByType = 'Agent';
  } else if (!isThirdParty) {
    createDraftClaimPayload.claimContacts = [
      {
        contact: {
          contactId: 1,
          contactType: 'Person',
          firstName: customerInfo.firstName,
          isThirdParty: isReporterOnPolicy ? 'NO' : 'YES',
          sourceSystemIdentifier: sourceSystemIdentifier,
          lastName: customerInfo.lastName,
          newPrimaryPhoneEmail: {
            country: 'United States',
            area: _get(customerInfo, 'phoneNumber', '').slice(0, 3),
            number: _get(customerInfo, 'phoneNumber', '')
              .replace(/[^0-9]/g, '')
              .slice(3, 10),
            type: 'Cell',
            emailAddress: customerInfo.emailAddress,
            emailType: 'Primary',
          },
        },
      },
      {
        contact: {
          contactId: 2,
          contactType: 'Person',
          firstName: primaryNamedInsured.firstName,
          sourceSystemIdentifier: sourceSystemIdentifier,
          isThirdParty: 'NO',
          lastName: primaryNamedInsured.lastName,
        },
      },
    ];
    createDraftClaimPayload.reportedBy = {
      contactId: 1,
    };
    createDraftClaimPayload.insuredContactInfo = {
      contactId: 2,
    };
    createDraftClaimPayload.reportedByType = _get(fnolInfo, 'reporterInfo.relationshipToInsured')
      ? _get(fnolInfo, 'reporterInfo.relationshipToInsured')
      : isReporterOnPolicy
        ? 'Self'
        : 'Claimant';
  }

  // add reportedBy
  if (!!isThirdParty && !!_get(options, 'addReportedBy')) {
    createDraftClaimPayload.reportedBy = {
      contactId: _get(options, 'reportedBy', 1),
    };
    createDraftClaimPayload.reportedByType = 'Claimant';
  }

  // add vehicle details
  const vehicleDetailsPayload: VehicleDetails[] = [];
  if (!isThirdParty || !!_get(options, 'addVehicleDetails')) {
    if (_get(fnolInfo, 'riskInfo.vehicleTypeName') === DriverDefaults.VEHICLE_NOT_ON_POLICY) {
      // Always include vehicle details for "vehicle not on policy" so converseDB gets initialized with vehicle info
      vehicleDetailsPayload.push({
        vehLossIsThirdParty: 'NO',
        vehTypeVin: '',
        vehTypeYear: _get(fnolInfo, 'riskInfo.year'),
        vehTypeMake: _get(fnolInfo, 'riskInfo.make'),
        vehTypeModel: _get(fnolInfo, 'riskInfo.model'),
      });

      createDraftClaimPayload.vehicleNotOnPolicy = true;
    } else {
      vehicleDetailsPayload.push({
        vehLossIsThirdParty: 'NO',
        vehTypeVin: _get(fnolInfo, 'riskInfo.vin'),
        vehTypeYear: _get(fnolInfo, 'riskInfo.year'),
        vehTypeMake: _get(fnolInfo, 'riskInfo.make'),
        vehTypeModel: _get(fnolInfo, 'riskInfo.model'),
      });
    }
  }

  // Always update the driver contact since we're now always sending vehicleDetails
  const updateVehicle = true;

  // Only add a contactId if we have a driver
  if (_get(fnolInfo, 'driverInfo.firstName') || _get(fnolInfo, 'driverInfo.lastName')) {
    // Check if driver and insured aren't the same person. If they aren't, make a new
    // contact for driver, else make sure insured is contact (contactId: 1)
    if (isReporterOnPolicy && createDraftClaimPayload.claimContacts) {
      if (
        !isSamePerson(
          _get(fnolInfo, 'driverInfo.firstName', ''),
          _get(fnolInfo, 'driverInfo.lastName', ''),
          _get(customerInfo, 'firstName', ''),
          _get(customerInfo, 'lastName', '')
        )
      ) {
        createDraftClaimPayload.claimContacts.push({
          contact: {
            contactId: 2,
            contactType: 'Person',
            firstName: _get(fnolInfo, 'driverInfo.firstName', ''),
            lastName: _get(fnolInfo, 'driverInfo.lastName', ''),
            isThirdParty: 'NO',
            sourceSystemIdentifier: sourceSystemIdentifier,
          },
        });

        if (updateVehicle) {
          vehicleDetailsPayload[0].vehLossDriverContact = {
            contactId: 2,
          };
        }
      } else if (updateVehicle) {
        vehicleDetailsPayload[0].vehLossDriverContact = {
          contactId: 1,
        };
      }
    } else {
      // If we already have seperate reporter and insured, check them both and
      // add a third contact if driver is neither of them
      if (
        updateVehicle &&
        isSamePerson(
          _get(fnolInfo, 'driverInfo.firstName', ''),
          _get(fnolInfo, 'driverInfo.lastName', ''),
          _get(customerInfo, 'firstName', ''),
          _get(customerInfo, 'lastName', '')
        )
      ) {
        vehicleDetailsPayload[0].vehLossDriverContact = {
          contactId: 1,
        };
      } else if (
        updateVehicle &&
        isSamePerson(
          _get(fnolInfo, 'driverInfo.firstName', ''),
          _get(fnolInfo, 'driverInfo.lastName', ''),
          _get(primaryNamedInsured, 'firstName', ''),
          _get(primaryNamedInsured, 'lastName', '')
        )
      ) {
        vehicleDetailsPayload[0].vehLossDriverContact = {
          contactId: 2,
        };
      } else if (createDraftClaimPayload.claimContacts) {
        createDraftClaimPayload.claimContacts.push({
          contact: {
            contactId: 3,
            contactType: 'Person',
            firstName: _get(fnolInfo, 'driverInfo.firstName', ''),
            lastName: _get(fnolInfo, 'driverInfo.lastName', ''),
            isThirdParty: 'NO',
            sourceSystemIdentifier: sourceSystemIdentifier,
          },
        });
        if (updateVehicle) {
          vehicleDetailsPayload[0].vehLossDriverContact = {
            contactId: 3,
          };
        }
      }
    }
  }

  // Append vehicleDetails to create draftclaim payload for Auto.
  if (_get(fnolInfo, 'riskInfo.generalizedProductType') === GenericProductType.Auto) {
    const payloadWithVehicleDetails = {
      ...createDraftClaimPayload,
      vehicleDetails: vehicleDetailsPayload,
    };
    return payloadWithVehicleDetails;
  } else {
    // Property payload
    return createDraftClaimPayload;
  }
}

export function mapLocalDraftToCCFormat(localDraft: any, claimNumber: string) {
  const ccFormat = {
    claimNumber: claimNumber,
    claimSystem: 'GWCC',
    atFaultPercentage: 0,
    catIndicator: false,
    claimStatusCode: 'draft',
    claimStatusDescription: 'Draft',
    coverageInQuestion: false,
    fatalityIndicator: false,
    incidentOnlyClaimIndicator: false,
    isAttorneyRepresented: false,
    isDocumentArchived: false,
    legalIndicator: false,
    lossDate: format(new Date(localDraft.lossDate.split(' ')[0]), 'MM/dd/yyyy'),
    openDate: format(new Date(localDraft.openDate.split(' ')[0]), 'MM/dd/yyyy'),
    securedClaim: false,
    policyNumber: localDraft.policyNumber,
    policyType: localDraft.lobCode,
    policyPartnerID: '',
    policyPartnerBrandID: '',
    totalLossAmountPaid: 0,
    totalReservedAmount: 0,
    lossLocationAddress: localDraft.lossLocationAddress,
    newDigitalClaim: true,
    vehicleDetail: _get(localDraft, 'vehicleDetails', []).map((detail: any) => {
      return {
        Vin: detail.vehTypeVin,
        Year: detail.vehTypeYear,
        Make: detail.vehTypeMake,
        Model: detail.vehTypeModel,
      };
    }),
  };

  return ccFormat;
}

export function getVehicleDamageAnswer(damageType: string): string {
  switch (damageType) {
    case DamageTypes.NO_DAMAGE:
      return 'No Damage';
    case DamageTypes.TOTAL_LOSS:
      return 'Damage All Over';
    case DamageTypes.PARTIAL_DAMAGE:
      return 'Some Damage';
    default:
      return 'Some Damage';
  }
}

export function setupDraftResumeFields(converseResponse: any, policyInfo: any[]): any {
  // Find reporter info
  const reporterInfo = {
    contactReferenceKey: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    relationshipToInsured: 'Self',
  };

  const reporterContactId = _get(converseResponse, 'draftClaimInfo.reportedBy.contactId', 1);
  const reporterContact = _get(converseResponse, 'draftClaimInfo.claimContacts', []).find(
    (contact: any) => contact.contact.contactId === reporterContactId
  );
  if (reporterContact) {
    reporterInfo.firstName = reporterContact.contact.firstName;
    reporterInfo.lastName = reporterContact.contact.lastName;
    reporterInfo.emailAddress = _get(reporterContact, 'contact.newPrimaryPhoneEmail.emailAddress', '');
    reporterInfo.phoneNumber =
      _get(reporterContact, 'contact.newPrimaryPhoneEmail.area', '') +
      _get(reporterContact, 'contact.newPrimaryPhoneEmail.number', '');
  }

  // Find driver info
  const driverInfo = {
    id: '',
    firstName: '',
    lastName: '',
  };

  const firstPartyDrivenVehicle = _get(converseResponse, 'draftClaimInfo.vehicleDetails', []).find(
    (vehicle: any) => !!vehicle.vehLossDriverContact && vehicle.vehLossIsThirdParty === 'NO'
  );
  if (firstPartyDrivenVehicle) {
    const driverContactId = _get(firstPartyDrivenVehicle, 'vehLossDriverContact.contactId', -1);
    const driverContact = _get(converseResponse, 'draftClaimInfo.claimContacts', []).find(
      (contact: any) => contact.contact.contactId === driverContactId
    );
    if (driverContact) {
      driverInfo.firstName = driverContact.contact.firstName;
      driverInfo.lastName = driverContact.contact.lastName;
      driverInfo.id = driverContactId;
    }
  }

  // Date location Info
  const dateLocationInfo = {
    lossDate: _get(converseResponse, 'draftClaimInfo.lossDate', ''),
    city: _get(converseResponse, 'draftClaimInfo.lossLocationAddress[0].city', ''),
    state: _get(converseResponse, 'draftClaimInfo.lossLocationAddress[0].state', ''),
  };

  // Find Risk Info
  let riskInfo = undefined;
  if (firstPartyDrivenVehicle) {
    riskInfo = policyInfo.find(
      (risk: any) =>
        risk.policyNumber === _get(converseResponse, 'draftClaimInfo.policyNumber', -1) &&
        risk.vin === firstPartyDrivenVehicle.vehTypeVin
    );
  } else {
    riskInfo = policyInfo.find(
      (risk: any) =>
        risk.policyNumber === _get(converseResponse, 'draftClaimInfo.policyNumber', -1) &&
        risk.address.addressLine1 === _get(converseResponse, 'draftClaimInfo.lossLocationAddress[0].addressLine1', '')
    );
  }

  return {
    riskInfo: riskInfo,
    reporterInfo: reporterInfo,
    driverInfo: driverInfo,
    dateLocationInfo: dateLocationInfo,
  };
}

export function createInitiateDynamicQuestionPayload(
  draftClaimNumber: string,
  createDraftClaimPayload: CreateDraftClaimModel
) {
  return {
    draftClaimNumber: draftClaimNumber.replace(/-/g, ''),
    payload: {
      answer: null,
      draftClaimInfo: createDraftClaimPayload,
    },
  };
}

function buildLossLocationAddress(fnolInfo: any, dateTime: any): LossLocationAddress[] {
  if (_get(fnolInfo, 'riskInfo.generalizedProductType') === GenericProductType.Home) {
    return [
      {
        addressLine1: _get(fnolInfo, 'riskInfo.address.addressLine1'),
        addressLine2: _get(fnolInfo, 'riskInfo.address.addressLine2'),
        city: _get(fnolInfo, 'riskInfo.address.city'),
        state: _get(fnolInfo, 'riskInfo.address.state'),
        zip: _get(fnolInfo, 'riskInfo.address.zipCode5'),
      },
    ];
  } else {
    return [
      {
        city: _get(dateTime, 'city', _get(fnolInfo, 'riskInfo.address.city', '')),
        state: _get(dateTime, 'state', _get(fnolInfo, 'riskInfo.address.state', '')),
      },
    ];
  }
}

function isSamePerson(
  person1FirstName: string,
  person1LastName: string,
  person2FirstName: string,
  person2LastName: string
): boolean {
  return (
    (person1FirstName.toUpperCase().split(' ').includes(person2FirstName.toUpperCase()) &&
      person1LastName.toUpperCase().split(' ').includes(person2LastName.toUpperCase())) ||
    (person2FirstName.toUpperCase().split(' ').includes(person1FirstName.toUpperCase()) &&
      person2LastName.toUpperCase().split(' ').includes(person1LastName.toUpperCase())) ||
    (person1FirstName.toUpperCase() === person2FirstName.toUpperCase() &&
      person1LastName.toUpperCase() === person2LastName.toUpperCase())
  );
}

function getThirdpartyContactAndVehiclePayload(contactForm: any, index: number): ThirdPartyContactPayloadModel {
  return {
    claimContact: {
      contact: {
        contactId: index + 1,
        firstName: _get(contactForm, 'firstName', ''),
        lastName: _get(contactForm, 'lastName', ''),
        newAddress: [
          {
            addressLine1: _get(contactForm, 'addressLine1', ''),
            addressLine2: _get(contactForm, 'addressLine2', ''),
            city: _get(contactForm, 'city', ''),
            state: _get(contactForm, 'state', ''),
            zip: _get(contactForm, 'zipCode', ''),
          },
        ],
        newPrimaryPhoneEmail: {
          country: 'United States',
          area: _get(contactForm, 'phoneNumber', '')
            .replace(/[^0-9]/g, '')
            .slice(0, 3),
          number: _get(contactForm, 'phoneNumber', '')
            .replace(/[^0-9]/g, '')
            .slice(3, 10),
          type: 'Cell',
          emailAddress: _get(contactForm, 'emailAddress', ''),
          emailType: 'Primary',
        },
        contactType: 'Person',
        isThirdParty: 'YES',
      },
    },
    vehicleDetail: _get(contactForm, 'vehicleInvolved', '')
      ? {
          vehLossDriverContact: {
            contactId: index + 1,
          },
          vehLossIsThirdParty: 'YES',
          vehTypeYear: _get(contactForm, 'vehicleYear', ''),
          vehTypeMake: _get(contactForm, 'vehicleMake', ''),
          vehTypeModel: _get(contactForm, 'vehicleModel', ''),
          vehTypeVin: uuid().slice(0, 17),
        }
      : undefined,
    isThirdPartyInjured: _get(contactForm, 'personInjured') ? 'YES' : '',
  };
}

export function createSubmitQuestionPayload(
  selectedAnswer: string,
  payload: SubmitQuestionStoreModel
): SubmitQuestionApiModel {
  if (payload.questionType === FnolQuestionTypes.SINGLE_SELECT) {
    return {
      answer: {
        questionId: payload.questionId,
        singleSelect: selectedAnswer,
      },
      draftClaimInfo: null,
    };
  } else if (payload.questionType === FnolQuestionTypes.MULTI_SELECT) {
    return {
      answer: {
        questionId: payload.questionId,
        multipleSelect: selectedAnswer,
      },
      draftClaimInfo: null,
    };
  } else if (payload.questionType === FnolQuestionTypes.FREE_FORM) {
    return {
      answer: {
        questionId: payload.questionId,
        freeForm: selectedAnswer,
      },
      draftClaimInfo: null,
    };
  } else if (payload.questionType === FnolQuestionTypes.INSURED_CONTACT_TYPE) {
    return {
      answer: {
        questionId: payload.questionId,
        insuredContactType: {
          email: _get(payload, 'emailAddress', ''),
          areaCode: _get(payload, 'phoneNumber', '')
            .replace(/[^0-9]/g, '')
            .slice(0, 3),
          phoneNumber: _get(payload, 'phoneNumber', '')
            .replace(/[^0-9]/g, '')
            .slice(3, 10),
          preferredContactType: _get(payload, 'preferredContactType', ''),
        },
      },
      draftClaimInfo: null,
    };
  } else if (payload.questionType === FnolQuestionTypes.VEHICLE_DAMAGE_PICKER) {
    return {
      answer: {
        questionId: payload.questionId,
        vehicleDamagePicker: {
          damageType: _get(payload, 'vehicleDamagePicker.damageType', ''),
          vehDamageDamageDescription: _get(payload, 'vehicleDamagePicker.vehDamageDamageDescription', ''),
          vehicleDamages: _get(payload, 'vehicleDamagePicker.vehicleDamages', []),
        },
      },
      draftClaimInfo: null,
    };
  } else if (payload.questionType === FnolQuestionTypes.THIRD_PARTY_CONTACT_FORM) {
    const thirdPartyContacts: ThirdPartyContactPayloadModel[] = [];

    payload.thirdPartyContactForm?.forEach((contact: any, index: number) => {
      thirdPartyContacts.push(getThirdpartyContactAndVehiclePayload(contact, (payload.numberOfContacts || 1) + index));
    });

    return {
      answer: {
        questionId: payload.questionId,
        thirdPartyContactForm: thirdPartyContacts,
      },
      draftClaimInfo: null,
    };
  } else if (payload.questionType === FnolQuestionTypes.CLAIMANT_CONTACT_TYPE) {
    const claimantInfo: ClaimantContactPayloadModel = mapClaimantForm(
      payload.claimantContactForm,
      _get(payload, 'numberOfContacts', 0)
    );

    return {
      answer: {
        questionId: payload.questionId,
        claimantContactForm: claimantInfo,
      },
      draftClaimInfo: payload.draftClaimInfo,
    };
  } else {
    return { answer: null, draftClaimInfo: null };
  }
}

export function createGlassPayload(payload: any, successUrl: string): ClaimGlassOnly {
  return {
    policyNumber: _get(payload, 'riskInfo.policyNumber'),
    lossDate: _get(payload, 'dateLocationInfo.lossDate'),
    returnURL: successUrl,
    returnURL2: successUrl,
    firstName: _get(payload, 'reporterInfo.firstName'),
    lastName: _get(payload, 'reporterInfo.lastName'),
    address1: _get(payload, 'riskInfo.address.addressLine1'),
    address2: _get(payload, 'riskInfo.address.addressLine2'),
    city: _get(payload, 'riskInfo.address.city'),
    state: _get(payload, 'riskInfo.address.state'),
    zip: _get(payload, 'riskInfo.address.zipCode'),
    email: _get(payload, 'reporterInfo.emailAddress'),
    phoneNumber: checkAndReplaceDummyPhoneNumber(_get(payload, 'reporterInfo.phoneNumber')),
  };
}

function mapClaimantForm(form: any, numberOfContacts: number): ClaimantContactPayloadModel {
  return {
    claimContact: {
      contact: {
        contactId: numberOfContacts,
        firstName: form.customerFirstName,
        lastName: form.customerLastName,
        newAddress: [
          {
            addressLine1: form.addressLine1,
            addressLine2: form.addressLine2,
            city: form.city,
            state: form.stateCode,
            zip: form.zip,
          },
        ],
        newPrimaryPhoneEmail: {
          country: 'United States',
          area: _get(form, 'customerPhoneNumber', '')
            .replace(/[^0-9]/g, '')
            .slice(0, 3),
          number: _get(form, 'customerPhoneNumber', '')
            .replace(/[^0-9]/g, '')
            .slice(3, 10),
          type: 'Cell',
          emailAddress: _get(form, 'customerEmail', ''),
          emailType: 'Primary',
        },
        contactType: 'Person',
        isThirdParty: 'YES',
      },
    },
    insuranceContact: {
      contact: {
        contactId: numberOfContacts + 1,
        firstName: form.adjusterFirstName,
        lastName: form.adjusterLastName,
        newPrimaryPhoneEmail: {
          country: 'United States',
          area: _get(form, 'adjusterPhoneNumber', '')
            .replace(/[^0-9]/g, '')
            .slice(0, 3),
          number: _get(form, 'adjusterPhoneNumber', '')
            .replace(/[^0-9]/g, '')
            .slice(3, 10),
          type: 'Cell',
          emailAddress: _get(form, 'adjusterEmail', ''),
          emailType: 'Primary',
        },
        contactType: 'Person',
        isThirdParty: 'YES',
      },
    },
    vehicleDetail: form.isaVehicleInvolved
      ? {
          vehLossDriverContact: {
            contactId: numberOfContacts,
          },
          vehLossIsThirdParty: 'YES',
          vehTypeYear: _get(form, 'vehicleYear', ''),
          vehTypeMake: _get(form, 'vehicleMake', ''),
          vehTypeModel: _get(form, 'vehicleModel', ''),
          vehTypeVin: uuid().slice(0, 17),
        }
      : null,
    policyNumber: form.policyNumber,
    claimNumber: form.claimNumber,
    insuranceCompanyName: form.insuranceCompanyName,
    insuranceCompanyPhoneExtension: form.adjusterPhoneExtensionNumber,
    isDriver: form.isCustomerAlsoDriver ? 'YES' : '',
  };
}

function getSourceSystem(partner: string, mobileSourceSystem?: string): string {
  switch (partner) {
    case OpCo.AMFAM:
      switch (mobileSourceSystem?.toLowerCase()) {
        case 'myaccountios':
          return 'MyAmfam iOS';
        case 'myaccountandroid':
          return 'MyAmfam Android';
        default:
          return 'MyAccountWebApplication';
      }
    case OpCo.MSA:
      return 'MSAWebApplication';
    case 'aficsadjuster':
      return 'AdjusterWebApplication';
    case 'amfamagency':
      return 'AgentWebApplication';
    case OpCo.MSA_THIRD_PARTY:
      return '3rdPartyMSAWeb';
    case OpCo.AMFAM_THIRD_PARTY:
      return '3rdPartyAmFamWeb';
    case 'osiThirdParty':
      return '3rdPartyOSIWeb_Ext';
    case 'midvaleThirdParty':
      return '3rdPartyMidvaleWeb';
    default:
      return 'MyAccountWebApplication';
  }
}
